import React, { useState } from 'react';
import { AlertCircle, CheckCircle, XCircle, RefreshCw } from 'lucide-react';

export default function TokenRefreshTester() {
  const [apiUrl, setApiUrl] = useState('https://sayul-digital-backend.onrender.com/api/v1/auth/refresh');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const testRefresh = async () => {
    setLoading(true);
    setResult(null);

    try {
      console.log('🧪 Testing refresh endpoint:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        credentials: 'include', // Send cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({})
      });

      const data = await response.json();
      
      // Get all cookies (visible ones)
      const cookies = document.cookie.split(';').map(c => c.trim());
      
      // Get response headers
      const headers = {};
      response.headers.forEach((value, key) => {
        headers[key] = value;
      });

      setResult({
        status: response.status,
        ok: response.ok,
        responseBody: data,
        cookies: cookies,
        headers: headers,
        setCookieHeader: response.headers.get('set-cookie'), // Usually null in browser
      });

      console.log('📊 Response:', {
        status: response.status,
        data,
        cookies,
        headers
      });

    } catch (error) {
      setResult({
        error: true,
        message: error.message,
        details: error.toString()
      });
      console.error('❌ Test failed:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            <RefreshCw className="text-blue-600" />
            Token Refresh Tester
          </h1>
          <p className="text-gray-600 mb-6">
            Test your backend's refresh token endpoint to see what it returns
          </p>

          {/* API URL Input */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Refresh Endpoint URL
            </label>
            <input
              type="text"
              value={apiUrl}
              onChange={(e) => setApiUrl(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="http://localhost:5000/api/v1/auth/refresh"
            />
          </div>

          {/* Test Button */}
          <button
            onClick={testRefresh}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <RefreshCw className="animate-spin" size={20} />
                Testing...
              </>
            ) : (
              <>
                <RefreshCw size={20} />
                Test Refresh Endpoint
              </>
            )}
          </button>

          {/* Instructions */}
          <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
              <AlertCircle size={18} />
              Before Testing:
            </h3>
            <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
              <li>Make sure you're logged in to your app first</li>
              <li>Open DevTools (F12) and go to the Network tab</li>
              <li>Click the button above</li>
              <li>Check the results below</li>
            </ol>
          </div>

          {/* Results */}
          {result && (
            <div className="mt-8 space-y-4">
              {/* Status */}
              <div className={`p-4 rounded-lg ${result.error ? 'bg-red-50 border border-red-200' : result.ok ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  {result.error ? (
                    <>
                      <XCircle className="text-red-600" size={20} />
                      <span className="text-red-900">Request Failed</span>
                    </>
                  ) : result.ok ? (
                    <>
                      <CheckCircle className="text-green-600" size={20} />
                      <span className="text-green-900">Status: {result.status} OK</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="text-yellow-600" size={20} />
                      <span className="text-yellow-900">Status: {result.status}</span>
                    </>
                  )}
                </h3>
                {result.error && (
                  <p className="text-sm text-red-700">{result.message}</p>
                )}
              </div>

              {/* Response Body */}
              {!result.error && result.responseBody && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">📦 Response Body:</h3>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded text-sm overflow-x-auto">
{JSON.stringify(result.responseBody, null, 2)}
                  </pre>
                  
                  {/* Analysis */}
                  <div className="mt-4 space-y-2">
                    <h4 className="font-semibold text-gray-800">Analysis:</h4>
                    {result.responseBody.accessToken ? (
                      <div className="flex items-start gap-2 text-green-700">
                        <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm">✅ <strong>accessToken</strong> found in response</span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 text-red-700">
                        <XCircle size={18} className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm">❌ No <strong>accessToken</strong> in response</span>
                      </div>
                    )}
                    
                    {result.responseBody.refreshToken ? (
                      <div className="flex items-start gap-2 text-green-700">
                        <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm">✅ <strong>refreshToken</strong> found in response body (this should be in a cookie instead!)</span>
                      </div>
                    ) : (
                      <div className="flex items-start gap-2 text-yellow-700">
                        <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
                        <span className="text-sm">⚠️ No <strong>refreshToken</strong> in response body (this is OK if it's in an HTTP-only cookie)</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Cookies */}
              {!result.error && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2">🍪 Visible Cookies:</h3>
                  {result.cookies.length > 0 ? (
                    <div className="space-y-1">
                      {result.cookies.map((cookie, i) => (
                        <div key={i} className="text-sm font-mono bg-white p-2 rounded border border-gray-200">
                          {cookie}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-600">No visible cookies (HTTP-only cookies won't show here)</p>
                  )}
                  <p className="text-xs text-gray-500 mt-3">
                    ℹ️ HTTP-only cookies (refreshToken) won't appear here. Check DevTools → Application → Cookies to see them.
                  </p>
                </div>
              )}

              {/* Instructions for checking cookies */}
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-900 mb-2">🔍 How to Check HTTP-only Cookies:</h3>
                <ol className="list-decimal list-inside text-sm text-indigo-800 space-y-1">
                  <li>Open DevTools (F12)</li>
                  <li>Go to <strong>Application</strong> tab (Chrome) or <strong>Storage</strong> tab (Firefox)</li>
                  <li>Click <strong>Cookies</strong> in the left sidebar</li>
                  <li>Select your domain (localhost:5000 or your API domain)</li>
                  <li>Look for a cookie named <strong>refreshToken</strong></li>
                  <li>Check if the <strong>HttpOnly</strong> column is checked ✅</li>
                </ol>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}